import type {
  OddPlayerPolicy,
  PairingPriority,
  PlayParticipantGender,
  SocialPlayMode,
} from '../types/playEvent';

export interface PairingPlayer {
  id: string;
  gender?: PlayParticipantGender | null;
  skillLevel?: string | null;
  playerRating?: number | null;
  dateOfBirth?: Date | null;
  /** Prior partner ids from earlier rounds */
  priorPartnerIds?: string[];
  /** Checked in as willing to play singles when doubles cannot seat everyone. */
  prefersSingles?: boolean;
}

export interface PairingTeam {
  playerIds: string[];
}

export interface PairingMatch {
  sideA: PairingTeam;
  sideB: PairingTeam;
  sittingOut: string[];
}

export interface SocialRoundPairingInput {
  players: PairingPlayer[];
  socialPlayMode: SocialPlayMode;
  oddPlayerPolicy: OddPlayerPolicy;
  side: 'singles' | 'doubles' | 'mixed';
  pairingPriorities: PairingPriority[];
  courtCount: number;
}

const SKILL_RANK: Record<string, number> = {
  beginner: 1,
  intermediate: 2,
  advanced: 3,
  expert: 4,
  any: 2,
};

function skillRank(level?: string | null): number {
  if (!level) return 2;
  return SKILL_RANK[level.toLowerCase()] ?? 2;
}

function ageYears(dob?: Date | null, now = new Date()): number | null {
  if (!dob) return null;
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age;
}

function priorityContribution(
  priority: PairingPriority,
  a: PairingPlayer,
  b: PairingPlayer
): number {
  if (priority === 'mixed_gender') {
    if (a.gender && b.gender && a.gender !== b.gender && a.gender !== 'other' && b.gender !== 'other') {
      return 10;
    }
    return 0;
  }
  if (priority === 'skill_balance') {
    const diff = Math.abs(skillRank(a.skillLevel) - skillRank(b.skillLevel));
    return diff === 1 ? 8 : diff === 0 ? 4 : 0;
  }
  if (priority === 'player_rating_balance') {
    const ratingA = a.playerRating;
    const ratingB = b.playerRating;
    if (ratingA != null && ratingB != null) {
      const diff = Math.abs(ratingA - ratingB);
      return Math.max(0, 10 - diff * 2);
    }
    return 0;
  }
  if (priority === 'age_balance') {
    const ageA = ageYears(a.dateOfBirth);
    const ageB = ageYears(b.dateOfBirth);
    if (ageA != null && ageB != null) {
      const gap = Math.abs(ageA - ageB);
      return gap >= 10 ? 6 : gap >= 5 ? 3 : 0;
    }
    return 0;
  }
  if (priority === 'minimize_repeat_partners') {
    const aPrior = a.priorPartnerIds ?? [];
    const bPrior = b.priorPartnerIds ?? [];
    if (aPrior.includes(b.id) || bPrior.includes(a.id)) {
      return -100;
    }
    return 5;
  }
  return 0;
}

function pairScore(
  a: PairingPlayer,
  b: PairingPlayer,
  priorities: PairingPriority[]
): number {
  let score = 0;
  const count = priorities.length;
  for (let index = 0; index < count; index++) {
    const weight = count - index;
    score += priorityContribution(priorities[index], a, b) * weight;
  }
  return score;
}

function buildDoublesTeams(
  players: PairingPlayer[],
  priorities: PairingPriority[]
): { teams: PairingTeam[]; unpairedPlayerIds: string[] } {
  const remaining = [...players];
  const teams: PairingTeam[] = [];
  while (remaining.length >= 2) {
    let bestI = 0;
    let bestJ = 1;
    let bestScore = -Infinity;
    for (let i = 0; i < remaining.length; i++) {
      for (let j = i + 1; j < remaining.length; j++) {
        const s = pairScore(remaining[i], remaining[j], priorities);
        if (s > bestScore) {
          bestScore = s;
          bestI = i;
          bestJ = j;
        }
      }
    }
    const a = remaining.splice(Math.max(bestI, bestJ), 1)[0];
    const b = remaining.splice(Math.min(bestI, bestJ), 1)[0];
    teams.push({ playerIds: [a.id, b.id] });
  }
  return { teams, unpairedPlayerIds: remaining.map((player) => player.id) };
}

function bestPairs(players: PairingPlayer[], priorities: PairingPriority[]): PairingTeam[] {
  return buildDoublesTeams(players, priorities).teams;
}

function americanSinglesMatches(players: PairingPlayer[], courtCount: number): PairingMatch[] {
  const ids = players.map((p) => p.id);
  const matches: PairingMatch[] = [];
  for (let i = 0; i + 1 < ids.length; i += 2) {
    matches.push({
      sideA: { playerIds: [ids[i]] },
      sideB: { playerIds: [ids[i + 1]] },
      sittingOut: [],
    });
    if (matches.length >= courtCount) break;
  }
  const used = new Set(matches.flatMap((m) => [...m.sideA.playerIds, ...m.sideB.playerIds]));
  const sittingOut = ids.filter((id) => !used.has(id));
  if (matches.length > 0) matches[0].sittingOut = sittingOut;
  return matches;
}

function sortSinglesPreferredFirst(players: PairingPlayer[]): PairingPlayer[] {
  return [...players].sort((a, b) => {
    const aRank = a.prefersSingles ? 0 : 1;
    const bRank = b.prefersSingles ? 0 : 1;
    return aRank - bRank;
  });
}

function buildSinglesMatches(
  players: PairingPlayer[],
  priorities: PairingPriority[],
  courtCount: number
): PairingMatch[] {
  const remaining = sortSinglesPreferredFirst(players);
  const matches: PairingMatch[] = [];

  while (remaining.length >= 2 && matches.length < courtCount) {
    let bestI = 0;
    let bestJ = 1;
    let bestScore = -Infinity;
    for (let i = 0; i < remaining.length; i++) {
      for (let j = i + 1; j < remaining.length; j++) {
        const score = pairScore(remaining[i], remaining[j], priorities);
        if (score > bestScore) {
          bestScore = score;
          bestI = i;
          bestJ = j;
        }
      }
    }

    const first = remaining.splice(Math.max(bestI, bestJ), 1)[0];
    const second = remaining.splice(Math.min(bestI, bestJ), 1)[0];
    matches.push({
      sideA: { playerIds: [first.id] },
      sideB: { playerIds: [second.id] },
      sittingOut: [],
    });
  }

  const sittingOut = remaining.map((player) => player.id);
  if (matches.length > 0) {
    matches[0].sittingOut = sittingOut;
  }
  return matches;
}

function singlesMatches(
  players: PairingPlayer[],
  courtCount: number,
  priorities: PairingPriority[]
): PairingMatch[] {
  return buildSinglesMatches(players, priorities, courtCount);
}

function doublesMatchesFromTeams(teams: PairingTeam[], courtCount: number): PairingMatch[] {
  const matches: PairingMatch[] = [];
  for (let i = 0; i + 1 < teams.length && matches.length < courtCount; i += 2) {
    matches.push({
      sideA: teams[i],
      sideB: teams[i + 1],
      sittingOut: [],
    });
  }
  const usedTeams = matches.length * 2;
  const sittingOut = teams.slice(usedTeams).flatMap((t) => t.playerIds);
  if (matches.length > 0) matches[0].sittingOut = sittingOut;
  return matches;
}

function doublesWithFallbackSingles(
  players: PairingPlayer[],
  priorities: PairingPriority[],
  courtCount: number
): PairingMatch[] {
  const preferSinglesPlayers = players.filter((player) => player.prefersSingles);
  const preferDoublesPlayers = players.filter((player) => !player.prefersSingles);

  let { teams, unpairedPlayerIds } = buildDoublesTeams(preferDoublesPlayers, priorities);
  let singlesQueue: PairingPlayer[] = [
    ...preferSinglesPlayers,
    ...preferDoublesPlayers.filter((player) => unpairedPlayerIds.includes(player.id)),
  ];

  while (teams.length < 2 && singlesQueue.length >= 2) {
    const borrowed = singlesQueue.splice(-2, 2);
    teams.push({ playerIds: borrowed.map((player) => player.id) });
  }

  const matches: PairingMatch[] = [];
  let courtsUsed = 0;

  for (let i = 0; i + 1 < teams.length && courtsUsed < courtCount; i += 2) {
    matches.push({
      sideA: teams[i],
      sideB: teams[i + 1],
      sittingOut: [],
    });
    courtsUsed += 1;
  }

  const usedTeams = matches.length * 2;
  const unusedTeamPlayerIds = new Set(
    teams.slice(usedTeams).flatMap((team) => team.playerIds)
  );
  singlesQueue = sortSinglesPreferredFirst([
    ...singlesQueue,
    ...preferDoublesPlayers.filter((player) => unusedTeamPlayerIds.has(player.id)),
  ]);

  while (courtsUsed < courtCount && singlesQueue.length >= 2) {
    matches.push({
      sideA: { playerIds: [singlesQueue[0].id] },
      sideB: { playerIds: [singlesQueue[1].id] },
      sittingOut: [],
    });
    singlesQueue = singlesQueue.slice(2);
    courtsUsed += 1;
  }

  if (matches.length > 0) {
    matches[0].sittingOut = singlesQueue.map((player) => player.id);
  }

  return matches;
}

/**
 * Maximum pairings to create for a social rotate round from roster size.
 * Pair all players who can be matched in the round; physical courts are assigned
 * cyclically when there are more pairings than reserved courts.
 */
export function resolveSocialRoundMatchCount(playerCount: number): number {
  if (playerCount < 2) return 0;
  return Math.floor(playerCount / 2);
}

export function generateSocialRoundPairings(input: SocialRoundPairingInput): PairingMatch[] {
  const { players, socialPlayMode, oddPlayerPolicy, pairingPriorities, courtCount } = input;
  if (players.length < 2) return [];

  const effectiveMode =
    socialPlayMode === 'prefer_doubles' && players.length < 4 ? 'singles_only' : socialPlayMode;

  if (effectiveMode === 'singles_only') {
    if (players.length % 2 === 1 && oddPlayerPolicy === 'sit_out') {
      const sitOut = players[players.length - 1].id;
      const rest = players.slice(0, -1);
      const matches = singlesMatches(rest, courtCount, pairingPriorities);
      if (matches[0]) matches[0].sittingOut = [sitOut, ...matches[0].sittingOut];
      return matches;
    }
    if (players.length % 2 === 1 && oddPlayerPolicy === 'american_singles') {
      return americanSinglesMatches(players, courtCount);
    }
    return singlesMatches(players, courtCount, pairingPriorities);
  }

  // doubles_only or prefer_doubles with enough players
  if (players.length < 4 && oddPlayerPolicy === 'fallback_singles') {
    return singlesMatches(players, courtCount, pairingPriorities);
  }
  if (players.length < 4 && oddPlayerPolicy === 'american_singles') {
    return americanSinglesMatches(players, courtCount);
  }

  const teams = bestPairs(players, pairingPriorities);
  if (teams.length < 2) {
    if (oddPlayerPolicy === 'sit_out' || oddPlayerPolicy === 'fallback_doubles_sit_out') {
      return [];
    }
    return singlesMatches(players, courtCount, pairingPriorities);
  }

  if (oddPlayerPolicy === 'fallback_singles') {
    return doublesWithFallbackSingles(players, pairingPriorities, courtCount);
  }

  return doublesMatchesFromTeams(teams, courtCount);
}
